import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { useEscapeKey } from '../../../hooks/useEscapeKey';
import type { TreatmentCatalogItem, TreatmentCatalogUpdate } from '../../../utils/treatmentCatalogApi';

const TREATMENT_TYPE_LABELS: Record<string, string> = {
  WOUND_CARE: 'Wound Care',
  DENTAL: 'Dental',
  GROOMING: 'Grooming',
  FLUID_THERAPY: 'Fluid Therapy',
  BANDAGING: 'Bandaging',
  INJECTION: 'Injection',
  OTHER: 'Other',
};

export const TreatmentCatalogItemDrawer: React.FC<{
  isOpen: boolean;
  item: TreatmentCatalogItem;
  onClose: () => void;
  onSave: (data: TreatmentCatalogUpdate) => Promise<void>;
}> = ({ isOpen, item, onClose, onSave }) => {
  const [name, setName] = useState('');
  const [treatmentType, setTreatmentType] = useState('OTHER');
  const [description, setDescription] = useState('');
  const [duration, setDuration] = useState<string>('');
  const [requiresSedation, setRequiresSedation] = useState(false);
  const [sedationNotes, setSedationNotes] = useState('');
  const [notes, setNotes] = useState('');
  const [stockTracked, setStockTracked] = useState(false);
  const [saving, setSaving] = useState(false);

  useEscapeKey(onClose, isOpen);

  useEffect(() => {
    if (!isOpen) return;
    setName(item.name || '');
    setTreatmentType(item.treatment_type || 'OTHER');
    setDescription(item.description || '');
    setDuration(item.default_duration_minutes != null ? String(item.default_duration_minutes) : '');
    setRequiresSedation(Boolean(item.requires_sedation));
    setSedationNotes(item.sedation_notes || '');
    setNotes(item.notes || '');
    setStockTracked(Boolean(item.is_stock_tracked));
  }, [isOpen, item.id]);

  if (!isOpen) return null;

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const payload: TreatmentCatalogUpdate = {
        name: name.trim(),
        treatment_type: treatmentType,
        description: description || undefined,
        default_duration_minutes: duration === '' ? undefined : Number(duration),
        requires_sedation: requiresSedation,
        sedation_notes: sedationNotes || undefined,
        notes: notes || undefined,
        is_stock_tracked: stockTracked,
      };
      await onSave(payload);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="absolute right-0 top-0 h-full w-full max-w-xl bg-white shadow-2xl border-l border-gray-200 flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-gray-900 truncate">Update treatment</div>
            <div className="text-xs text-gray-500 mt-0.5 truncate">Update treatment details and inventory settings.</div>
          </div>
          <button onClick={onClose} className="p-2 rounded-md hover:bg-gray-100 text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-auto px-6 py-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <select
              value={treatmentType}
              onChange={(e) => setTreatmentType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {Object.entries(TREATMENT_TYPE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Default Duration (minutes)</label>
            <input
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex items-center gap-3 pt-1">
            <input
              type="checkbox"
              checked={requiresSedation}
              onChange={(e) => setRequiresSedation(e.target.checked)}
              className="w-4 h-4"
            />
            <div className="text-sm text-gray-900">Requires sedation</div>
          </div>

          {requiresSedation ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sedation Notes</label>
              <textarea
                value={sedationNotes}
                onChange={(e) => setSedationNotes(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          ) : null}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Internal Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex items-center gap-3 pt-1">
            <button
              type="button"
              onClick={() => setStockTracked((v) => !v)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                stockTracked ? 'bg-green-500' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  stockTracked ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <div>
              <div className="text-sm font-medium text-gray-900">Track as inventory</div>
              <div className="text-xs text-gray-500">Enables on-hand, lots, and inventory reporting.</div>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !name.trim()}
            className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
};


